import { Option, Period } from '@spike/model';
import { fetchPetServicesThunk, NewBookingsStatus } from '@spike/new-bookings-action';
import { showErrorThunk } from '@spike/notifications-action';
import { abandonedBookingsClientFilterOptions } from 'components/Bookings2/Abandoned/AbandonedBookings';
import { push } from 'connected-react-router';
import { useApiClientWrapper, useWaitForReduxChange } from 'hooks';
import { useOrganizationMarketplaces } from 'hooks/useOrganizationMarketplaces';
import useTimeZone from 'hooks/useTimeZone';
import { BookingAttempt } from 'model/BookingAttempt';
import { AppointmentMultiple, DataVaraible, Discount, ItemData, PetServiceWrapper } from 'model/NewMultipleBooking';
import { stepFilterBuckets } from 'model/OnlineBooking';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TwoWaySMSPath } from 'routes/Paths';
import { RootState, store } from 'store';

interface AbandonedBookingsContextProps {
    // Data
    isLoading: Map<string, boolean>;
    bookingAttempts: Array<BookingAttempt>;

    // Pagination
    size: number;
    page: number;
    totalPages: number;
    setSize: (size: number) => void;
    setPage: (page: number) => void;

    // Search query
    query: string;
    setQuery: (query: string) => void;

    // Type filter
    type: string;
    setType: (type: string) => void;

    // Step filter (bucket ids — see stepFilterBuckets)
    selectedSteps: Array<string>;
    setSelectedSteps: (steps: Array<string>) => void;

    // Client filters
    clientFilterOptions: Array<Option<string>>;
    selectedClientFilters: Array<string>;
    setSelectedClientFilters: (filters: Array<string>) => void;

    // Date period
    datePeriod: Period;
    setDatePeriod: (period: Period) => void;

    //Locations
    marketplaceIds: Array<number>;
    setMarketplaceIds: (marketplaceIds: Array<number>) => void;

    // Create booking
    showCreateBooking: boolean;
    setShowCreateBooking: (show: boolean) => void;
    bookingAttempt: BookingAttempt | null;
    bookingAttemptAppointment: Array<AppointmentMultiple>;

    // Create evaluation
    showCreateEvaluation: boolean;
    setShowCreateEvaluation: (show: boolean) => void;

    // Actions
    sendSmsHandler: (bookingAttempt: BookingAttempt) => void;
    openCreateBookingHandler: (bookingAttempt: BookingAttempt) => void;
    closeCreateBookingHandler: () => void;
}

export const AbandonedBookingsContext = React.createContext<AbandonedBookingsContextProps | null>(null);

// Combine "YYYY-MM-DD" + "10:00 am" / "3:30 pm" into a Moment in the marketplace's TZ.
// Falls back to a date-only Moment if the time string is unparseable.
const combineDateAndTime = (date: string, time: string | undefined, timeZone: string): Moment => {
    if (!time) return moment.tz(date, 'YYYY-MM-DD', timeZone);
    const parsed = moment.tz(`${date} ${time}`, ['YYYY-MM-DD h:mm a', 'YYYY-MM-DD H:mm'], timeZone);
    return parsed.isValid() ? parsed : moment.tz(date, 'YYYY-MM-DD', timeZone);
};

export const AbandonedBookingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { waitForStateChange } = useWaitForReduxChange();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const timeZone = useTimeZone();

    const today = moment().startOf('day');

    const { bookingAttempts, pagination } = useSelector((state: RootState) => state.bookingAttempts);

    const { services: addOnServices } = useSelector((state: RootState) => state.addOnServices);

    const [isLoading, setIsLoading] = React.useState<Map<string, boolean>>(new Map());

    // Pagination
    const [size, setSize] = React.useState(pagination?.size ?? 10);
    const [page, setPage] = React.useState(pagination?.page ?? 1);

    // Filters
    const [type, setType] = React.useState('abandoned');
    const [query, setQuery] = React.useState<string>('');
    const [selectedSteps, setSelectedSteps] = React.useState<Array<string>>(stepFilterBuckets.map(bucket => bucket.id));
    const [selectedClientFilters, setSelectedClientFilters] = React.useState<Array<string>>(
        abandonedBookingsClientFilterOptions.map(option => option.id)
    );
    const [datePeriod, setDatePeriod] = React.useState<Period>({ from: today, to: today });

    const [marketplaceIds, setMarketplaceIds] = React.useState<Array<number>>(organizationMarketplaces.map(m => m.id));

    // Create booking
    const [showCreateBooking, setShowCreateBooking] = React.useState<boolean>(false);

    // Create evaluation
    const [showCreateEvaluation, setShowCreateEvaluation] = React.useState<boolean>(false);

    // Booking attempt
    const [bookingAttempt, setBookingAttempt] = React.useState<BookingAttempt | null>(null);
    const [bookingAttemptAppointment, setBookingAttemptAppointment] = React.useState<Array<AppointmentMultiple>>([]);

    // Actions
    const sendSmsHandler = React.useCallback(
        (bookingAttempt: BookingAttempt) => {
            dispatch(push(`${TwoWaySMSPath}?customerId=${bookingAttempt.customerId}`));
        },
        [dispatch]
    );

    const openCreateBookingHandler = React.useCallback(
        async (attempt: BookingAttempt) => {
            const bookingItems = attempt.bookingData.bookingItems;

            if (!bookingItems || bookingItems.length === 0) {
                setShowCreateBooking(true);
                setBookingAttempt(attempt);
                return;
            }

            setIsLoading(prev => {
                const newMap = new Map(prev);
                newMap.set(attempt.id, true);
                return newMap;
            });

            try {
                const attemptAppointments: Array<AppointmentMultiple> = [];

                for (const item of bookingItems) {
                    if (!item.petId || !item.serviceId) continue;

                    const serviceType = item.serviceType;
                    let date: Moment | undefined;
                    let dateEnd: Moment | undefined;
                    let staffId: number | undefined;
                    let flow: ItemData['flow'];

                    if (serviceType === 'grooming') {
                        if (!item.date || !item.time) continue;
                        date = moment(`${item.date} ${item.time}`, 'YYYY-MM-DD HH:mm A').tz(timeZone, true);
                        staffId = item.staffId;
                        flow = 'grooming';
                    } else if (serviceType === 'daycare') {
                        if (!item.date) continue;
                        date = combineDateAndTime(item.date, item.dropOffTime, timeZone);
                        dateEnd = item.pickUpTime
                            ? combineDateAndTime(item.date, item.pickUpTime, timeZone)
                            : undefined;
                        flow = 'daycare';
                    } else if (serviceType === 'boarding') {
                        if (!item.startDate) continue;
                        date = moment.tz(item.startDate, 'YYYY-MM-DD', timeZone);
                        dateEnd = item.endDate ? moment.tz(item.endDate, 'YYYY-MM-DD', timeZone) : undefined;
                        flow = 'boarding';
                    } else {
                        continue;
                    }

                    if (!date) continue;

                    dispatch(fetchPetServicesThunk(apiClientWrapper, item.petId));

                    await waitForStateChange(
                        state => state.newBookings.status,
                        NewBookingsStatus.FetchPetServicesSuccess
                    );

                    const petServices = store.getState().newBookings.petServices;
                    const petService = petServices.find(ps => ps.service.id === item.serviceId);

                    if (!petService) continue;

                    const wrapper: PetServiceWrapper = {
                        service: petService.service,
                        price: petService.price,
                        durationInMinutes: petService.durationInMinutes,
                        businessArea: petService.businessArea,
                        color: petService.color,
                        roomUnitId: item.roomUnitId,
                        feedings: item.feedings,
                        medications: item.medications
                    };

                    attemptAppointments.push({
                        petId: item.petId,
                        clientId: attempt.customerId,
                        createdByStaffId: staffId ?? 0,
                        notes: attempt.bookingData.notes,
                        variableData: [
                            {
                                staffId,
                                date,
                                dateEnd,
                                service: wrapper,
                                itemData: {
                                    flow,
                                    discount: { value: 0, type: 'amount' as Discount['type'] }
                                }
                            },
                            ...item.addons.reduce<Array<DataVaraible>>((addons, addon) => {
                                const addOnService = addOnServices.find(a => a.id === addon.id);
                                if (addOnService) {
                                    const quantity = addon.quantity ?? 1;
                                    const unitPrice = addOnService.price ?? 0;
                                    addons.push({
                                        addOn: addOnService,
                                        staffId,
                                        date: date!,
                                        itemData: {
                                            discount: { value: 0, type: 'amount' as Discount['type'] },
                                            quantity,
                                            price: unitPrice,
                                            total: unitPrice * quantity
                                        }
                                    });
                                }
                                return addons;
                            }, [])
                        ]
                    });
                }

                setBookingAttemptAppointment(attemptAppointments);
                setBookingAttempt(attempt);
                setShowCreateBooking(true);
            } catch (error) {
                dispatch(showErrorThunk('One or more services could not be fetched. Please try again.'));
                console.warn('Failed to fetch pet services:', error);
            } finally {
                setIsLoading(prev => {
                    const newMap = new Map(prev);
                    newMap.set(attempt.id, false);
                    return newMap;
                });
            }
        },
        [apiClientWrapper, addOnServices, dispatch, timeZone, waitForStateChange]
    );

    const closeCreateBookingHandler = React.useCallback(() => {
        setShowCreateBooking(false);
        setBookingAttempt(null);
    }, []);

    return (
        <AbandonedBookingsContext.Provider
            value={{
                // Data
                bookingAttempts,
                isLoading,

                // Pagination
                size,
                page,
                totalPages: pagination?.totalPages ?? 1,
                setPage,
                setSize,
                query,
                setQuery,

                // Filters
                type,
                setType,
                selectedSteps,
                setSelectedSteps,
                clientFilterOptions: abandonedBookingsClientFilterOptions,
                selectedClientFilters,
                setSelectedClientFilters,
                datePeriod,
                setDatePeriod,
                marketplaceIds,
                setMarketplaceIds,

                // Create booking
                showCreateBooking,
                setShowCreateBooking,

                // Create evaluation
                showCreateEvaluation,
                setShowCreateEvaluation,

                // Booking attempt
                bookingAttempt,
                bookingAttemptAppointment,

                // Actions
                sendSmsHandler,
                openCreateBookingHandler,
                closeCreateBookingHandler
            }}
        >
            {children}
        </AbandonedBookingsContext.Provider>
    );
};

export const useAbandonedBookings = (): AbandonedBookingsContextProps => {
    const context = React.useContext(AbandonedBookingsContext);

    if (!context) {
        throw new Error('useAbandonedBookings must be used within an AbandonedBookingsProvider');
    }

    return context;
};
